import { ConditionDescription, ConditionType, IConditionScores, QuestionnaireData, QuestionnaireDataStep } from '@contracts';
import { calculateAge } from '@utils/date';
import { conditionPainArray, conditionSignsAndSymptomsArray } from '../components/PeriodPainReport/Conditions/data/ConditionsLists';
import { PainConsultationData, PainkillerContext } from './periodPainConsultation';
import { formatReportDate } from './periodPainReport';

const BLANK_IMAGE = 'https://images.prismic.io/ecommerce-website/cdbabd8f-20b4-47fa-a14a-ac0676ed8873_1x1-00000000.png?auto=compress,format';
const TICK_IMAGE = 'https://images.prismic.io/ecommerce-website/Z31IpJbqstJ99Ii0_TickIcon.png?auto=format,compress';
const YELLOW_EXCLAMATION_IMAGE =
  'https://images.prismic.io/ecommerce-website/3e4d49fd-61ea-47b0-9716-7e8fda5c9176_ExclamationYellow.png?auto=format,compress';

type StringIndexedObject = { [key: string]: string };

export interface IGeneratePPCReportPDFContent {
  [key: string]: any;
}

const formatString = (input: string): string =>
  input
    ?.split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// Maps to handle rewrites and pain sensations
const rewrites = new Map<string, string>([
  ['before-period', 'Before their period'],
  ['after-period', 'After their period'],
  ['when-i-ovulate', 'When they ovulate'],
  ['throughout', 'Throughout their period'],
  ['pressure-in-pelvis', 'Pressure in the pelvis'],
  ['age-<8', 'Less than 8 years old'],
  ['age->13', 'More than 13 years old'],
  ['age-unsure', 'Not sure'],
  ['age-8-10', '8-10 years old'],
  ['age-11-13', '11-13 years old'],
  ['shortly-after-first-period', "Shortly after the patient's first period (up to a year)"],
  ['a-few-years-after-first-period', "A few years after the patient's first period"],
  ['more-than-10-years-after-first-period', "More than 10 years after the patient's first period"],
  ['pain-1', 'Very minor annoyance - occasional minor twinges'],
  ['pain-2', 'Minor annoyance - occasional strong twinges'],
  ['pain-3', 'Annoying enough to be distracting'],
  ['pain-4', 'Can be ignored if the patient is really involved in their work, but still distracting'],
  ['pain-5', "Can't be ignored for more than 30 minutes"],
  ['pain-6', "Can't be ignored for any length of time, but the patient can still go to work and participate in social activities"],
  ['pain-7', 'Makes it difficult to concentrate, interferes with sleep, but the patient can still function with effort'],
  ['pain-8', 'Physical activity severely limited, the patient can read and converse with effort, feeling nausea and dizziness'],
  ['pain-9', 'Unable to speak, crying out or moaning uncontrollably – near delirium'],
  ['pain-10', 'Unconscious - pain makes the patient pass out'],
  ['<1-day', 'Less than 1 day'],
  ['1-3-days', 'Between 1-3 days'],
  ['between-1-3-days', 'Between 1-3 days'],
  ['>3', 'More than 3 days'],
  ['<21', 'Less than 21 days'],
  ['21-25', '21-25 days'],
  ['26-31', '26-31 days'],
  ['32+', '32+ days'],
  ['1-2', '1-2 days'],
  ['3-5', '3-5 days'],
  ['6-8', '6-8 days'],
  ['9+', '9+ days'],
  ['pain-lasts-varies', 'Varies'],
  ['intermittent-pain-description', 'Pain that comes and goes in waves, with periods of little or no pain in between'],
  ['constant-pain-description', "Pain that is present all the time and doesn't go away"],
  ['variable-intensity-description', 'Pain that changes in severity over time, ranging from mild to severe'],
  ['consistent-intensity-description', 'The severity of the pain doesn’t change over time'],
  ['period-scale-spotting', '1 out of 5 - Spotting'],
  ['period-scale-light', '2 out of 5 - Light'],
  ['period-scale-medium', '3 out of 5 - Medium'],
  ['period-scale-heavy', '4 out of 5 - Heavy'],
  ['period-scale-very-heavy', '5 out of 5 - Very heavy'],
  ['period-predict-very-regular', 'Very regular and predictable'],
  ['period-predict-usually-regular', 'Usually regular, occasionally starting sooner or later than expected'],
  ['period-predict-irregular', 'Irregular — it’s difficult to predict when they will start'],
  ['period-predict-very-rare', 'My periods are very rare, but I still menstruate from time to time (less than 8 cycles per year)'],
  ['period-predict-no-periods-contraception', 'I’m on hormonal contraception and I don’t get periods because of it'],
  ['period-predict-no-periods', "I don't get periods at all"],
  ['hair-loss-mild', '1/3 - Mild'],
  ['hair-loss-moderate', '2/3 - Moderate'],
  ['hair-loss-severe', '3/3 - Severe'],
  ['otc-painkillers', 'OTC painkillers'],
  ['prescription-painkillers', 'Prescription painkillers'],
  ['birth-control-pills', 'Birth control pills'],
  ['hormonal-coil-IUS', 'IUD'],
  ['heat-therapy', 'Heat therapy'],
  ['massages', 'Massages'],
  ['acupuncture', 'Acupuncture'],
  ['tens-machines', 'TENS machines'],
  ['cbd-tampons', 'CBD tampons'],
  ['oral-cbd-capsules-oil', 'Oral CBD capsules/oil'],
  ['thc', 'THC (cannabis)'],
  ['pelvic-floor-stretches', 'Pelvic floor stretching routine'],
  ['cbd', 'CBD'],
  ['ibuprofen', 'Ibuprofen (Nurofen®, Advil®, Motrin®)'],
  ['aspirin', 'Aspirin (Aspro®, Bayer®)'],
  ['paracetamol', 'Paracetamol (acetaminophen, Panadol®, Tylenol®)'],
  ['naproxen', 'Naproxen (Naprosyn®, Aleve®)'],
  ['mefenamic-acid', 'Mefenamic Acid (Ponstan®, Ponstel®)'],
  ['dihydrocodeine', 'Dihydrocodeine (DF118® Forte, Panadeine® Forte)'],
  ['16:8', '16:8 (8 hour feeding window)'],
  ['18:6', '18:6 (6 hour feeding window)'],
  ['20:4', '20:4 (4 hour feeding window)'],
  ['36-hour-fasts', '36-hour fasts'],
  ['occassionally', 'Occasionally (a few times per month)'],
  ['one-pack-per-week-less', 'One pack per week or less'],
  ['one-pack-every-2-3-days', 'One pack every 2-3 days'],
  ['2-3-times-week', '2-3 times a week'],
  ['2-4-times-month', '2-4 times a month'],
  ['sti-test', 'STI test']
]);

const painSensations = new Map<string, string>([
  ['dull-ache', 'A constant, mild to moderate pain that is spread out and difficult to pinpoint'],
  ['throbbing-pain', 'A rhythmic, pulsing pain'],
  ['sharp-pain', 'A sudden, acute pain that feels like stabbing or piercing'],
  ['burning-sensation', 'A persistent, uncomfortable sensation of heat or burning'],
  ['pressure-in-pelvis', 'A sensation of fullness or heaviness']
]);

const getPainScaleImage = new Map<string, string>([
  ['0', 'https://images.prismic.io/ecommerce-website/51593c30-bd50-4633-b3ca-4ca867bf9554_pain-scale-1.png?auto=compress,format'],
  ['1', 'https://images.prismic.io/ecommerce-website/51593c30-bd50-4633-b3ca-4ca867bf9554_pain-scale-1.png?auto=compress,format'],
  ['2', 'https://images.prismic.io/ecommerce-website/6e6247b0-4bd2-486f-b862-9c13f47bdb31_pain-scale-2.png?auto=compress,format'],
  ['3', 'https://images.prismic.io/ecommerce-website/b5ff662d-5203-41c6-a6b5-e328c70aa0a1_pain-scale-3.png?auto=compress,format'],
  ['4', 'https://images.prismic.io/ecommerce-website/a661c8b1-f4ca-4554-bed4-33e50a32ce32_pain-scale-4.png?auto=compress,format'],
  ['5', 'https://images.prismic.io/ecommerce-website/57836d38-6023-46c7-9341-ad009aa6552d_pain-scale-5.png?auto=compress,format'],
  ['6', 'https://images.prismic.io/ecommerce-website/0c66d4ef-5c83-47fc-9f4a-f18db8a00216_pain-scale-6.png?auto=compress,format'],
  ['7', 'https://images.prismic.io/ecommerce-website/5a3f1bb6-fd7a-4efe-bdc1-4ab0118b2a40_pain-scale-7.png?auto=compress,format'],
  ['8', 'https://images.prismic.io/ecommerce-website/20094b71-c24d-410d-825b-3a3990d3f88e_pain-scale-8.png?auto=compress,format'],
  ['9', 'https://images.prismic.io/ecommerce-website/c5a4db65-bf0d-4fc2-bbd1-6de6e3c65632_pain-scale-9.png?auto=compress,format'],
  ['10', 'https://images.prismic.io/ecommerce-website/5d516475-4e11-4efe-8e64-a7c35ec9594f_pain-scale-10.png?auto=compress,format']
]);

const getFlowImage = new Map<string, string>([
  ['spotting', 'https://images.prismic.io/ecommerce-website/3f5b9e8b-b5e6-46dd-9632-365322d6c62c_flow-scale-1-spotting.png?auto=compress,format'],
  ['light', 'https://images.prismic.io/ecommerce-website/a7654c86-cb3e-49bc-a516-b0faa521a70b_flow-scale-2-light.png?auto=compress,format'],
  ['medium', 'https://images.prismic.io/ecommerce-website/ed5cd085-1aa7-432d-b1df-032c492e2588_flow-scale-3-medium.png?auto=compress,format'],
  ['heavy', 'https://images.prismic.io/ecommerce-website/ea3824cc-df03-4b44-a920-b509560a9353_flow-scale-4-heavy.png?auto=compress,format'],
  ['very-heavy', 'https://images.prismic.io/ecommerce-website/40af34c2-058d-43ba-a9fc-1b92f8966312_flow-scale-5-very-heavy.png?auto=compress,format']
]);

const getHairLossImage = new Map<string, string>([
  ['mild', 'https://images.prismic.io/ecommerce-website/a2f58723-7170-4597-a84f-c646716eabd8_hair-loss-scale-1.png?auto=compress,format'],
  ['moderate', 'https://images.prismic.io/ecommerce-website/d4cde62e-6351-49ca-8094-42c44b0fb317_hair-loss-scale-2.png?auto=compress,format'],
  ['severe', 'https://images.prismic.io/ecommerce-website/bf4c7248-a833-4e37-b5a4-67122ef4cfb7_hair-loss-scale-3.png?auto=compress,format']
]);

const hairGrowthImage = new Map<string, string>([
  ['upper-lip-1', 'https://images.prismic.io/ecommerce-website/eff33c2a-1376-4ad2-b36b-fb9612190b82_upper-lip-hair-scale-2.png?auto=compress,format'],
  ['upper-lip-2', 'https://images.prismic.io/ecommerce-website/ea65975d-386a-441e-a9c8-e41a3e2e9d2b_upper-lip-hair-scale-3.png?auto=compress,format'],
  ['upper-lip-3', 'https://images.prismic.io/ecommerce-website/3f221cb7-95b8-4b72-a5e0-26054a4c8ac7_upper-lip-hair-scale-4.png?auto=compress,format'],
  ['upper-lip-4', 'https://images.prismic.io/ecommerce-website/a01c81ea-70a5-40b7-91a8-e3345daaed82_upper-lip-hair-scale-5.png?auto=compress,format'],
  ['chin-1', 'https://images.prismic.io/ecommerce-website/576eb413-91ee-48ac-81b3-fbb2b861294f_chin-hair-scale-2.png?auto=compress,format'],
  ['chin-2', 'https://images.prismic.io/ecommerce-website/1e283132-1591-47a2-b8b9-c6a8fdd69ce6_chin-hair-scale-3.png?auto=compress,format'],
  ['chin-3', 'https://images.prismic.io/ecommerce-website/56df86b5-519b-4ab2-888a-eb2d0772803d_chin-hair-scale-4.png?auto=compress,format'],
  ['chin-4', 'https://images.prismic.io/ecommerce-website/4f7c7f78-5607-4762-8e84-a07d6926134d_chin-hair-scale-5.png?auto=compress,format'],
  [
    'upper-chest-1',
    'https://images.prismic.io/ecommerce-website/a3f66749-84e9-4511-9578-ddc69c7a7139_upper-chest-hair-scale-2.png?auto=compress,format'
  ],
  [
    'upper-chest-2',
    'https://images.prismic.io/ecommerce-website/78be127d-0c7a-4c61-88cd-1966b80763d7_upper-chest-hair-scale-3.png?auto=compress,format'
  ],
  [
    'upper-chest-3',
    'https://images.prismic.io/ecommerce-website/565f6e10-7424-4246-95ce-43cded354f0b_upper-chest-hair-scale-4.png?auto=compress,format'
  ],
  [
    'upper-chest-4',
    'https://images.prismic.io/ecommerce-website/b60404ae-9953-4a50-9e71-07c1b21707b6_upper-chest-hair-scale-5.png?auto=compress,format'
  ],
  [
    'upper-abdomen-1',
    'https://images.prismic.io/ecommerce-website/c5c2bb77-0c45-4fd8-a203-f889e9d1a036_upper-abdomen-hair-scale-2.png?auto=compress,format'
  ],
  [
    'upper-abdomen-2',
    'https://images.prismic.io/ecommerce-website/c791500d-e66e-4dd7-8f19-4c7af2467dbf_upper-abdomen-hair-scale-3.png?auto=compress,format'
  ],
  [
    'upper-abdomen-3',
    'https://images.prismic.io/ecommerce-website/e54d3a2b-6ecc-4f9d-aeee-24a2a97cdcca_upper-abdomen-hair-scale-4.png?auto=compress,format'
  ],
  [
    'upper-abdomen-4',
    'https://images.prismic.io/ecommerce-website/b75d94c8-6b30-4a2e-9303-e9ebd414ea08_upper-abdomen-hair-scale-5.png?auto=compress,format'
  ],
  [
    'lower-abdomen-1',
    'https://images.prismic.io/ecommerce-website/fec1790c-43fb-498e-8db8-b73258cfcf71_lower-abdomen-hair-scale-2.png?auto=compress,format'
  ],
  [
    'lower-abdomen-2',
    'https://images.prismic.io/ecommerce-website/9a05d27e-a155-441e-a274-ec896aad77c3_lower-abdomen-hair-scale-3.png?auto=compress,format'
  ],
  [
    'lower-abdomen-3',
    'https://images.prismic.io/ecommerce-website/31f588dd-aa73-49ef-be95-f386e32dc967_lower-abdomen-hair-scale-4.png?auto=compress,format'
  ],
  [
    'lower-abdomen-4',
    'https://images.prismic.io/ecommerce-website/de370497-ea39-45cb-8d07-defc71da213d_lower-abdomen-hair-scale-5.png?auto=compress,format'
  ],
  [
    'upper-back-1',
    'https://images.prismic.io/ecommerce-website/bf1acb99-87c5-49d9-b216-d45821e5ddc9_upper-back-hair-scale-2.png?auto=compress,format'
  ],
  [
    'upper-back-2',
    'https://images.prismic.io/ecommerce-website/a2767844-44d4-4ce6-a8b8-e582323d2b07_upper-back-hair-scale-3.png?auto=compress,format'
  ],
  [
    'upper-back-3',
    'https://images.prismic.io/ecommerce-website/de81a665-c8e2-48fb-aca3-4ae02d38dfed_upper-back-hair-scale-4.png?auto=compress,format'
  ],
  [
    'upper-back-4',
    'https://images.prismic.io/ecommerce-website/77685d6f-b4f3-4666-8127-b553888a4bc8_upper-back-hair-scale-5.png?auto=compress,format'
  ],
  [
    'lower-back-1',
    'https://images.prismic.io/ecommerce-website/3f5822f6-ffdc-4848-b481-f1594c1bd44b_lower-back-hair-scale-2.png?auto=compress,format'
  ],
  [
    'lower-back-2',
    'https://images.prismic.io/ecommerce-website/d76811b2-40fc-4cc1-8c53-016a51b0f2ba_lower-back-hair-scale-3.png?auto=compress,format'
  ],
  [
    'lower-back-3',
    'https://images.prismic.io/ecommerce-website/562aa97b-400d-4f1f-bc69-687e09f928e1_lower-back-hair-scale-4.png?auto=compress,format'
  ],
  [
    'lower-back-4',
    'https://images.prismic.io/ecommerce-website/13454e79-c5c7-4a3b-b156-3696de12a916_lower-back-hair-scale-5.png?auto=compress,format'
  ],
  [
    'upper-arms-1',
    'https://images.prismic.io/ecommerce-website/0188049f-080f-44bd-9396-642d76b98c00_upper-arms-hair-scale-2.png?auto=compress,format'
  ],
  [
    'upper-arms-2',
    'https://images.prismic.io/ecommerce-website/07a93d09-29f1-4b97-b282-6506794a6854_upper-arms-hair-scale-3.png?auto=compress,format'
  ],
  [
    'upper-arms-3',
    'https://images.prismic.io/ecommerce-website/560db6f3-b47c-434a-98fe-1606a3c304c5_upper-arms-hair-scale-4.png?auto=compress,format'
  ],
  [
    'upper-arms-4',
    'https://images.prismic.io/ecommerce-website/b762ab5f-9590-450c-b87c-7bf4894888e4_upper-arms-hair-scale-5.png?auto=compress,format'
  ],
  ['thighs-1', 'https://images.prismic.io/ecommerce-website/027a8fce-8239-4a6a-ae2d-f82cf200df7d_thighs-hair-scale-2.png?auto=compress,format'],
  ['thighs-2', 'https://images.prismic.io/ecommerce-website/55724ed6-40c8-415b-90ae-5cb0a9cc57a6_thighs-hair-scale-3.png?auto=compress,format'],
  ['thighs-3', 'https://images.prismic.io/ecommerce-website/dc540424-11e9-459e-a00c-3c996e8788ef_thighs-hair-scale-4.png?auto=compress,format'],
  ['thighs-4', 'https://images.prismic.io/ecommerce-website/b15125b8-a97e-4a71-9e05-1793facfeaf2_thighs-hair-scale-5.png?auto=compress,format']
]);

const effectiveImage = new Map<string, string>([
  ['0', 'https://images.prismic.io/ecommerce-website/268f6271-3a37-4d65-a16c-228309788cf4_effective-not.png?auto=compress,format'],
  ['2', 'https://images.prismic.io/ecommerce-website/89b4e6f4-ebd1-4d7b-aec7-69fdde9fdf0c_effective-somewhat.png?auto=compress,format'],
  ['3', 'https://images.prismic.io/ecommerce-website/e6f1bcdf-a6f8-4efc-8199-7569256a1ec0_effective-very.png?auto=compress,format']
]);

const conditionRatingImage = new Map<string, string>([
  ['low', 'https://images.prismic.io/ecommerce-website/6f86e6ee-53c4-44b1-a320-f6d2f9d3dce7_probability-low.png?auto=compress,format'],
  ['medium', 'https://images.prismic.io/ecommerce-website/a3999b29-089e-4584-b368-d52dc50fcc5e_probability-medium.png?auto=compress,format'],
  ['high', 'https://images.prismic.io/ecommerce-website/52fe7a07-cae2-430a-9840-f98d328d94e9_probability-high.png?auto=compress,format'],
  ['diagnosed', 'https://images.prismic.io/ecommerce-website/164a743f-4be6-42db-861b-d681769996bd_probability-diagnosed.png?auto=compress,format']
]);

export const capitalizeFirstLetter = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const processHeight = (height: number) => {
  const feet = Math.floor(height / 30.48);
  const inches = Math.round((height % 30.48) / 2.54);
  return `${feet}’ ${inches} / ${height} cm`;
};

const processWeight = (weight: number) => {
  const lbs = Math.round(weight * 2.20462);
  return `${lbs} lbs / ${weight} kg`;
};

const processBMI = (weight: number, height: number) => {
  const bmiValue = weight / (height / 100) ** 2;
  const bmi = bmiValue.toFixed(1);

  let label = '';
  if (bmiValue < 18.5) {
    label = '(underweight)';
  } else if (bmiValue >= 18.5 && bmiValue < 25) {
    label = '(healthy weight)';
  } else if (bmiValue >= 25 && bmiValue < 30) {
    label = '(overweight)';
  } else {
    label = '(obese)';
  }

  return `${bmi} ${label}`;
};

const generatePainReliefTried = (data: { [key: string]: string }): { text: string; image: string }[] => {
  const result: { text: string; image: string }[] = [];
  Object.entries(data).forEach(([key, value]) => {
    if (value !== '1') {
      result.push({ text: rewrites.get(key) ?? key, image: effectiveImage.get(value) ?? '' });
    }
  });

  if (result.length === 0) {
    result.push({
      text: 'None',
      image: BLANK_IMAGE
    });
  }

  return result;
};

const generateMatchedTexts = (key: string, dataArray: ConditionDescription[], userArray: string[]) => {
  const filteredArray = dataArray.filter((item) => item.relatedConditions?.includes(key));
  if (!userArray) return [{ text: 'None' }];
  const sortedArray = filteredArray.filter((item) => userArray.includes(item.description));

  let returnArray = sortedArray.map((item) => ({
    text: item.description
  }));

  if (returnArray.length === 0) {
    returnArray = [{ text: 'None' }];
  }

  return returnArray;
};

export const generatePrescriptionPainkillerTexts = (painkillerContext: PainkillerContext) => {
  const array: { text: string }[] = [];

  if (painkillerContext.prescriptionPainkiller && painkillerContext.prescriptionPainkillerDoseExceeded === 'yes') {
    array.push({
      text: `Patient take ${painkillerContext.prescriptionPainkiller} more often than they have been prescribed`
    });
  } else if (painkillerContext.prescriptionPainkiller) {
    array.push({
      text: `Patient takes ${painkillerContext.prescriptionPainkiller} within the prescribed dose`
    });
  }

  return array;
};

const parseNumberFromString = (input: string): number | null => {
  const match = input.match(/(\d+)/);
  return match ? parseInt(match[0], 10) : null;
};

export const generateOTCPainkillerTexts = (painkillerContext: PainkillerContext) => {
  const array: { text: string }[] = [];

  if (painkillerContext.otcPainkiller) {
    const dose = parseNumberFromString(painkillerContext.otcPainkillerDose);
    if (painkillerContext.otcPainkillerDose?.includes('<') && dose) {
      array.push({
        text: `Patient takes less than ${dose}mg of ${painkillerContext.otcPainkiller} per day, which is within the recommended dose`
      });
    } else if (dose) {
      array.push({
        text: `Patient takes more than ${dose}mg of ${painkillerContext.otcPainkiller} per day, which is above the recommended dose`
      });
    }
    if (painkillerContext.otcPainkillerFrequency?.includes('<')) {
      array.push({
        text: `Patient takes ${painkillerContext.otcPainkiller} less than 4 hours apart, which is less than recommended`
      });
    } else {
      array.push({
        text: `Patient takes ${painkillerContext.otcPainkiller} more than 4 hours apart, which is recommended`
      });
    }
  }

  return array;
};

const generateLifestyleOther = (data: PainConsultationData) => {
  const consumption = data['engage-consumption'] as string[];
  const returnArray = [];

  if (consumption?.includes('alcohol')) {
    returnArray.push({
      text: 'Patient consumes alcohol',
      description: processData(data['how-often-alcohol'] as string, rewrites)
    });
  }
  if (consumption?.includes('smoking-cigarettes')) {
    returnArray.push({
      text: 'Patient smokes cigarettes',
      description: `${processData(data['how-often-cigarettes'] as string, rewrites)} - ${processData(
        data['how-many-cigarettes'] as string,
        rewrites
      )}`
    });
  }
  if (consumption?.includes('smoking-cannabis')) {
    returnArray.push({
      text: 'Patient smokes cannabis',
      description: processData(data['how-often-cannabis'] as string, rewrites)
    });
  }

  if (returnArray.length === 0) {
    returnArray.push({
      text: 'None'
    });
  }

  return returnArray;
};

// Helper function to process data
const processData = (data: string | string[], rewriteMap: Map<string, string>, contextMap?: Map<string, string>) => {
  if (!data || typeof data === 'number') return '';
  // If data is a string
  if (typeof data === 'string') {
    const rewriteText = rewriteMap.get(data) || capitalizeFirstLetter(data?.replace(/-/g, ' ')?.trim());
    return rewriteText;
  }
  // If data is an array
  return data.map((text) => {
    if (!text) return null;
    const rewriteText = rewriteMap.get(text) || capitalizeFirstLetter(text?.replace(/-/g, ' ')?.trim());
    const description = contextMap?.get(text);
    const returnObj: { text: string; description?: string } = { text: rewriteText };
    if (description) {
      returnObj.description = description;
    }
    return returnObj;
  });
};

const generateAllergies = (data: PainConsultationData) => {
  const allergicReactions = data['allergic-reaction'] as string[];
  const allergies = data.allergies as string;

  if (allergies !== '') {
    const index = allergicReactions?.indexOf('other');
    if (index !== -1) {
      allergicReactions[index] = allergies;
    }
  }
  return processData(allergicReactions, rewrites);
};

export const checkForRedFlags = (questionnaire: QuestionnaireData | undefined, data: PainConsultationData): string[] => {
  // Early returns for invalid input
  if (!questionnaire) return [];
  if (!questionnaire.steps || !Array.isArray(questionnaire.steps)) return [];

  const redFlagMap = new Map<string, string[]>();

  questionnaire.steps.forEach((step: QuestionnaireDataStep) => {
    // Skip invalid step data
    if (!step?.questions || !Array.isArray(step.questions)) return;

    step.questions.forEach((question) => {
      // Skip invalid questions or missing data
      if (!question?.referenceId || !question?.label || !question?.options || !(question.referenceId in data)) return;

      const answer = data[question.referenceId];

      // Handle array answers
      if (Array.isArray(answer)) {
        const redFlagAnswers: string[] = [];

        answer.forEach((selectedValue) => {
          if (typeof selectedValue !== 'string') return;

          const selectedOption = question.options?.find((opt) => opt?.value === selectedValue && typeof opt?.label === 'string');

          if (selectedOption?.redFlag) {
            redFlagAnswers.push(selectedOption.label);
          }
        });

        if (redFlagAnswers.length > 0) {
          redFlagMap.set(question.label, redFlagAnswers);
        }
      }
      // Handle string answers
      else if (typeof answer === 'string') {
        const selectedOption = question.options?.find((opt) => opt?.value === answer && typeof opt?.label === 'string');

        if (selectedOption?.redFlag) {
          redFlagMap.set(question.label, [selectedOption.label]);
        }
      }
    });
  });

  // Convert map to array of formatted strings
  return Array.from(redFlagMap).map(([question, answers]) => `${question} - ${answers.join(', ')}`);
};

export const generatePPCReportPDFContent = (
  questionnaire: QuestionnaireData,
  date: Date | null,
  data: PainConsultationData,
  conditions: IConditionScores,
  painkillerContext: PainkillerContext
) => {
  const pdfContent: { [key: string]: any } = {
    age: calculateAge(data['date-of-birth'] as string),
    date: formatReportDate(date || new Date()),
    customerName: '',
    dateOfBirth: data['date-of-birth'],
    periodPain: {},
    menstrualCycle: {},
    lifestyle: {},
    redFlags: []
  };

  const redFlags = checkForRedFlags(questionnaire, data);
  if (redFlags.length > 0) {
    pdfContent.redFlags = redFlags.map((flag) => ({ image: YELLOW_EXCLAMATION_IMAGE, text: flag }));
  } else {
    pdfContent.redFlags = [{ image: TICK_IMAGE, text: 'None' }];
  }

  // period pain
  pdfContent.periodPain.ageFirstPeriod = processData(`age-${data['age-first-period']}`, rewrites);
  pdfContent.periodPain.firstExperienceMenstrualCramps = processData(data['first-experience-menstrual-cramps'], rewrites);
  pdfContent.periodPain.painScale = `${data['how-severe-pain']}/10 on the Mankoski pain scale`;
  pdfContent.periodPain.painScaleText = processData(`pain-${data['how-severe-pain']}`, rewrites);
  pdfContent.periodPain.painScaleImage = getPainScaleImage.get(data['how-severe-pain']);
  pdfContent.periodPain.averageDuration = processData(`${data['how-long-does-pain-last']}`, rewrites);
  pdfContent.periodPain.painPattern = processData(data['describe-pain-pattern'], rewrites);
  pdfContent.periodPain.painPatternText =
    data['describe-pain-pattern'] !== 'not-sure' ? processData(`${data['describe-pain-pattern']}-description`, rewrites) : '';
  pdfContent.periodPain.painIntensity = processData(data['describe-pain-intensity'], rewrites);
  pdfContent.periodPain.painIntensityText =
    data['describe-pain-intensity'] !== 'not-sure' ? processData(`${data['describe-pain-intensity']}-description`, rewrites) : '';
  pdfContent.allergicResponses = generateAllergies(data);
  pdfContent.whenDoesPainOccur = processData(data['when-does-pain-occur'], rewrites);
  pdfContent.periodPainSensation = processData(data['describe-pain-sensation'], rewrites, painSensations);
  pdfContent.periodPainRadiatesOtherBodyParts = processData(data['pain-radiate-other-areas-body'], rewrites);
  pdfContent.sypmtomsDuringPeriod = processData(data['experience-other-symptoms'], rewrites);
  pdfContent.sypmtomsThroughoutCycle = processData(data['experience-symptoms-cycle'], rewrites);
  // TODO: resolve spelling issue - requires updated template

  // menstrual cycle
  pdfContent.menstrualCycle.predictability = processData(`period-predict-${data['period-predictability']}`, rewrites);
  pdfContent.menstrualCycle.flowOnHeaviest = data['period-scale'] ? processData(`period-scale-${data['period-scale']}`, rewrites) : 'Not applicable';
  pdfContent.menstrualCycle.flowImage = data['period-scale'] ? getFlowImage.get(data['period-scale']) : BLANK_IMAGE;
  pdfContent.menstrualCycle.averageLengthCycle = data['average-cycle-length']
    ? processData(data['average-cycle-length'], rewrites)
    : 'Not applicable';
  pdfContent.menstrualCycle.averageLengthBleed = data['bleed-length'] ? processData(data['bleed-length'], rewrites) : 'Not applicable';

  // symptoms
  const symptomsCycle = data['experience-symptoms-cycle'] as string[];
  if (symptomsCycle?.includes('hair-loss')) {
    pdfContent.hairLoss = [
      {
        text: processData(`hair-loss-${data['hair-loss-scale']}`, rewrites),
        image: getHairLossImage.get(data['hair-loss-scale'] as string)
      }
    ];
  }

  if (symptomsCycle?.includes('excessive-facial-or-body-hair')) {
    const hairGrowthScale = (data['hair-growth-scale'] || {}) as StringIndexedObject;
    pdfContent.hasHairOvergrowth = [{ text: 'Yes' }];

    const hairGrowthTicks: { text: string }[] = [];
    const hairGrowthCrosses: { text: string }[] = [];
    const hairGrowthSections: { text: string; image: string }[] = [];

    if (Object.keys(hairGrowthScale).length > 0) {
      Object.keys(hairGrowthScale).forEach((key) => {
        const value = hairGrowthScale[key];
        if (value === '0') {
          hairGrowthCrosses.push({
            text: processData(key, rewrites) as string
          });
        } else {
          hairGrowthTicks.push({
            text: processData(key, rewrites) as string
          });
          hairGrowthSections.push({
            text: processData(`excessive-hair-growth-on-${key}`, rewrites) as string,
            image: hairGrowthImage.get(`${key}-${value}`) as string
          });
        }
      });
      pdfContent.hasHairOvergrowth.excessiveHairGrowthTick = hairGrowthTicks;
      pdfContent.hasHairOvergrowth.excessiveHairGrowthCross = hairGrowthCrosses;
      pdfContent.excessiveHairGrowthTextImage = hairGrowthSections;
      const hairOvergrowthSum = Object.values(hairGrowthScale).reduce((total, num) => total + Number(num), 0);
      pdfContent.hirsutismScore = [{ text: `${hairOvergrowthSum} out of 36` }];
    }
  }

  // Medical History
  pdfContent.diagnosedGynaeConditions = processData(data['medical-professional-conditions'], rewrites);
  pdfContent.diagnosedInfectionsPastYear = processData(data['infections-within-last-year'], rewrites);
  pdfContent.diagnosedConditionsPrevious = processData(data['other-conditions'], rewrites);
  pdfContent.conditionsDirectRelatives = processData(data['gynaecological-conditions'], rewrites);
  pdfContent.examinationsGynaeConditons = processData(data['medical-examinations-related-to-gynaecological-conditions'], rewrites);
  pdfContent.screeningsPastYear = processData(data['medical-examinations-12-months'], rewrites);

  // Reproductive Health
  pdfContent.tryingToConceivePregnant = processData(data['currently-trying-to-conceive'], rewrites);
  pdfContent.notAbleToConceive = processData(data['unable-to-conceive-1-year'], rewrites);
  pdfContent.beenPregnantBefore = processData(data['previously-pregnant'], rewrites);
  const pregnancyHistory = data['pregnancy-history'] as string[];
  pdfContent.patientExperiencedPregnancies =
    pregnancyHistory?.length > 0 ? processData(data['pregnancy-history'], rewrites) : [{ text: 'Not applicable' }];
  const currentBirthControl = data['current-birth-control'];
  const otherBirthControl = data['current-birth-control-other'];
  pdfContent.contraceptionUsed = currentBirthControl === 'other' ? otherBirthControl : processData(data['current-birth-control'], rewrites);
  pdfContent.contraceptionChangedPastYear = processData(data['changed-contraception'], rewrites);

  // treatments
  const painReliefTried = data['forms-of-pain-relief-tried'] as StringIndexedObject;
  pdfContent.painReliefTried = generatePainReliefTried(painReliefTried);
  const otcPainkiller = data['otc-painkiller-usually-taken'] as string;
  const otcPainkillerName =
    otcPainkiller === 'other' ? (data['otc-painkiller-usually-taken-other'] as string) : formatString(otcPainkiller as string);
  pdfContent.otcPainkiller = painReliefTried['otc-painkillers'] !== '1' && otcPainkiller ? processData(otcPainkillerName, rewrites) : 'None';
  pdfContent.otcPainkillerDetails = generateOTCPainkillerTexts(painkillerContext);
  const prescriptionPainkiller = data['prescription-painkiller-usually-taken'] as string;
  const prescriptionPainkillerName =
    prescriptionPainkiller === 'other'
      ? (data['prescription-painkiller-usually-taken-other'] as string)
      : formatString(prescriptionPainkiller as string);

  pdfContent.prescriptionPainkiller =
    painReliefTried.prescriptionPainkiller !== '1' && prescriptionPainkiller ? processData(prescriptionPainkillerName, rewrites) : 'None';
  pdfContent.prescriptionPainkillerDetails = generatePrescriptionPainkillerTexts(painkillerContext);
  const adverseEffects = data['adverse-effects-from-pain-relief'] ?? data['adverse-affects-from-pain-relief'];
  pdfContent.adversePainkillerEffects = adverseEffects ? processData(adverseEffects, rewrites) : 'None';
  const otherMeds = data['currently-taking-any-other-meds'];
  pdfContent.otherMedication = otherMeds === 'yes' ? processData(data['currently-taking-any-other-meds-other'], rewrites) : 'None';

  // lifestyle
  pdfContent.lifestyle.height = processHeight(data.height);
  pdfContent.lifestyle.weight = processWeight(data.weight);
  pdfContent.lifestyle.bmi = processBMI(data.weight, data.height);
  pdfContent.ethnicity = processData(data.ethnicity, rewrites);
  pdfContent.lifestyle.dailyStress = processData(data.stress, rewrites);
  const diet = Array.isArray(data.diet) ? data.diet : [data.diet];
  pdfContent.diet = processData(diet, rewrites);
  const intermittentFasting = data['intermittent-fasting'];
  const intermittestFastingKind = data['kind-of-fasts'] as string[];
  const intermittestFastingFrequency = data['how-often-do-you-fast'] as string[];
  pdfContent.lifestyle.intermittentFasting =
    intermittentFasting === 'yes'
      ? `${processData(intermittestFastingKind, rewrites)} - ${processData(intermittestFastingFrequency, rewrites)}`
      : 'No';
  pdfContent.lifestyle.frequencyOfExercise = processData(data.exercise, rewrites);
  pdfContent.exercise = data.exercise !== 'rarely' ? processData(data['what-exercise'], rewrites) : [{ text: 'Not applicable' }];
  pdfContent.lifestyleOther = generateLifestyleOther(data);

  // conditions
  pdfContent.endoProbability = conditionRatingImage.get(conditions.scores[ConditionType.ENDOMETRIOSIS].riskScore as string);
  pdfContent.endoPain = generateMatchedTexts(ConditionType.ENDOMETRIOSIS, conditionPainArray, conditions['your-pain']);
  pdfContent.endoSymptoms = generateMatchedTexts(ConditionType.ENDOMETRIOSIS, conditionSignsAndSymptomsArray, conditions['symptoms-and-signs']);
  pdfContent.pcosProbability = conditionRatingImage.get(conditions.scores[ConditionType.PCOS].riskScore as string);
  pdfContent.pcosPain = generateMatchedTexts(ConditionType.PCOS, conditionPainArray, conditions['your-pain']);
  pdfContent.pcosSymptoms = generateMatchedTexts(ConditionType.PCOS, conditionSignsAndSymptomsArray, conditions['symptoms-and-signs']);
  pdfContent.adenomyosisProbability = conditionRatingImage.get(conditions.scores[ConditionType.ADENOMYOSIS].riskScore as string);
  pdfContent.adenomyosisPain = generateMatchedTexts(ConditionType.ADENOMYOSIS, conditionPainArray, conditions['your-pain']);
  pdfContent.adenomyosisSymptoms = generateMatchedTexts(ConditionType.ADENOMYOSIS, conditionSignsAndSymptomsArray, conditions['symptoms-and-signs']);
  pdfContent.fibroidsProbability = conditionRatingImage.get(conditions.scores[ConditionType.FIBROIDS].riskScore as string);
  pdfContent.fibroidsPain = generateMatchedTexts(ConditionType.FIBROIDS, conditionPainArray, conditions['your-pain']);
  pdfContent.fibroidsSymptoms = generateMatchedTexts(ConditionType.FIBROIDS, conditionSignsAndSymptomsArray, conditions['symptoms-and-signs']);
  pdfContent.pidProbability = conditionRatingImage.get(conditions.scores[ConditionType.PID].riskScore as string);
  pdfContent.pidPain = generateMatchedTexts(ConditionType.PID, conditionPainArray, conditions['your-pain']);
  pdfContent.pidSymptoms = generateMatchedTexts(ConditionType.PID, conditionSignsAndSymptomsArray, conditions['symptoms-and-signs']);
  pdfContent.pdProbability = conditionRatingImage.get(conditions.scores[ConditionType.PD].riskScore as string);
  pdfContent.pdPain = generateMatchedTexts(ConditionType.PD, conditionPainArray, conditions['your-pain']);
  pdfContent.pdSymptoms = generateMatchedTexts(ConditionType.PD, conditionSignsAndSymptomsArray, conditions['symptoms-and-signs']);

  return pdfContent;
};

export default generatePPCReportPDFContent;
